import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import { MainLayout } from './components/MainLayout';
import { PrivateRoute } from './components/PrivateRoute';
import { SuperAdminRoute } from './components/SuperAdminRoute';
import { CompanyAdminRoute } from './components/CompanyAdminRoute';
import { DeptAdminRoute } from './components/DeptAdminRoute';
import { AdminLayout } from './components/admin/AdminLayout';
import { CompanyAdminLayout } from './components/company-admin/CompanyAdminLayout';
import { DeptAdminLayout } from './components/dept-admin/DeptAdminLayout';
// Home page is no longer used - users are redirected to login
import { Login } from './pages/Login';
import { SignUp } from './pages/SignUp';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import { Profile } from './pages/Profile';
import AssessmentStart from './pages/assessment/AssessmentStart';
import AssessmentGuidance from './pages/assessment/AssessmentGuidance';
import AssessmentCategories from './pages/assessment/AssessmentCategories';
import AssessmentSymptoms from './pages/assessment/AssessmentSymptoms';
import AssessmentModuleRecommendation from './pages/assessment/AssessmentModuleRecommendation';
import AssessmentReport from './pages/assessment/AssessmentReport';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { UserManagement } from './pages/admin/UserManagement';
import { CompanyManagement } from './pages/admin/CompanyManagement';
import { DepartmentManagement } from './pages/admin/DepartmentManagement';
import { SuperAdminManagement } from './pages/admin/SuperAdminManagement';
import { EditorManagement } from './pages/admin/EditorManagement';
import { SymptomGroupManagement } from './pages/admin/SymptomGroupManagement';
import { GroupSymptomsPage } from './pages/admin/GroupSymptomsPage';
import { ApproachManagement } from './pages/admin/ApproachManagement';
import { SymptomManagement } from './pages/admin/SymptomManagement';
import { Statistics } from './pages/admin/Statistics';
import { Settings } from './pages/admin/Settings';
import { ImportExport } from './pages/admin/ImportExport';
import { InfluenceRules } from './pages/admin/InfluenceRules';
// Company Admin pages
import { CompanyAdminDashboard } from './pages/company-admin/CompanyAdminDashboard';
import { CompanyProfile } from './pages/company-admin/CompanyProfile';
import { CompanyDepartments } from './pages/company-admin/CompanyDepartments';
import { CompanyUsers } from './pages/company-admin/CompanyUsers';
// Department Admin pages
import { DeptAdminDashboard } from './pages/dept-admin/DeptAdminDashboard';
import { DeptAdminUsers } from './pages/dept-admin/DeptAdminUsers';
// Approach view (reference)
import ApproachView from './pages/ApproachView';

function AppContent() {
  // Enable inactivity logout for all users (except superadmin)
  useInactivityLogout();

  return (
    <BrowserRouter>
        <Routes>
          {/* Admin routes - Only accessible to superadmin */}
          <Route
            path="/admin"
            element={
              <SuperAdminRoute>
                <AdminLayout />
              </SuperAdminRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="superadmin" element={<SuperAdminManagement />} />
            <Route path="editors" element={<EditorManagement />} />
            <Route path="companies" element={<CompanyManagement />} />
            <Route path="departments" element={<DepartmentManagement />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="symptom-groups" element={<SymptomGroupManagement />} />
            <Route path="symptom-groups/:groupId/symptoms" element={<GroupSymptomsPage />} />
            <Route path="symptoms" element={<SymptomManagement />} />
            <Route path="approaches" element={<ApproachManagement />} />
            <Route path="statistics" element={<Statistics />} />
            <Route path="settings" element={<Settings />} />
            <Route path="import-export" element={<ImportExport />} />
            <Route path="influence-rules" element={<InfluenceRules />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          {/* Company Admin routes - Accessible to org_admin */}
          <Route
            path="/company-admin"
            element={
              <CompanyAdminRoute>
                <CompanyAdminLayout />
              </CompanyAdminRoute>
            }
          >
            <Route index element={<CompanyAdminDashboard />} />
            <Route path="profile" element={<CompanyProfile />} />
            <Route path="departments" element={<CompanyDepartments />} />
            <Route path="users" element={<CompanyUsers />} />
          </Route>

          {/* Department Admin routes - Accessible to dept_admin */}
          <Route
            path="/dept-admin"
            element={
              <DeptAdminRoute>
                <DeptAdminLayout />
              </DeptAdminRoute>
            }
          >
            <Route index element={<DeptAdminDashboard />} />
            <Route path="users" element={<DeptAdminUsers />} />
          </Route>

          {/* Main app routes */}
          <Route path="/" element={<MainLayout />}>
            <Route index element={<Login />} />
            <Route path="login" element={<Login />} />
            <Route path="signup" element={<SignUp />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route
              path="dashboard"
              element={
                <PrivateRoute requiredRoles={['superadmin']}>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route path="assessment" element={<Navigate to="/assessment/start" replace />} />
            <Route path="assessment/start" element={<PrivateRoute><AssessmentStart /></PrivateRoute>} />
            <Route path="assessment/guidance" element={<PrivateRoute><AssessmentGuidance /></PrivateRoute>} />
            <Route path="assessment/categories" element={<PrivateRoute><AssessmentCategories /></PrivateRoute>} />
            <Route path="assessment/symptoms" element={<PrivateRoute><AssessmentSymptoms /></PrivateRoute>} />
            <Route path="assessment/module" element={<PrivateRoute><AssessmentModuleRecommendation /></PrivateRoute>} />
            <Route path="assessment/report" element={<PrivateRoute><AssessmentReport /></PrivateRoute>} />
            <Route
              path="profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            {/* Approach reference pages */}
            <Route
              path="approaches/:number"
              element={
                <PrivateRoute>
                  <ApproachView />
                </PrivateRoute>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;